const TOKEN_STORAGE_KEY = 'ahime_token';
const USER_STORAGE_KEY = 'ahime_user';
const TOKEN_COOKIE_KEY = 'ahime_token';
const USER_COOKIE_KEY = 'ahime_user';
const COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;
const COOKIE_DOMAIN = (import.meta.env.VITE_AUTH_COOKIE_DOMAIN as string | undefined)?.trim() || '';

type StoredUser = {
  id: number;
  email: string;
  role: 'ADMIN' | 'CLIENT' | 'EMPLOYEE' | 'PARTNER';
  org_id?: number;
  profile_role?: string;
  name?: string;
};

type AuthSnapshot = {
  token: string;
  user: StoredUser;
};

function canUseBrowserStorage(): boolean {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined';
}

function canUseCookies(): boolean {
  return typeof document !== 'undefined';
}

function readCookie(name: string): string | null {
  if (!canUseCookies()) return null;
  const escaped = name.replace(/[-[\]/{}()*+?.\\^$|]/g, '\\$&');
  const match = document.cookie.match(new RegExp(`(?:^|; )${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string): void {
  if (!canUseCookies()) return;
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  const domain = COOKIE_DOMAIN ? `; Domain=${COOKIE_DOMAIN}` : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${domain}${secure}`;
}

function deleteCookie(name: string): void {
  if (!canUseCookies()) return;
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  const domain = COOKIE_DOMAIN ? `; Domain=${COOKIE_DOMAIN}` : '';
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax${domain}${secure}`;
}

export function readAuthSnapshot(): AuthSnapshot | null {
  try {
    if (canUseBrowserStorage()) {
      const token = localStorage.getItem(TOKEN_STORAGE_KEY);
      const userRaw = localStorage.getItem(USER_STORAGE_KEY);
      if (token && userRaw) {
        return { token, user: JSON.parse(userRaw) as StoredUser };
      }
    }

    const tokenCookie = readCookie(TOKEN_COOKIE_KEY);
    const userCookie = readCookie(USER_COOKIE_KEY);
    if (!tokenCookie || !userCookie) return null;

    const snapshot = { token: tokenCookie, user: JSON.parse(userCookie) as StoredUser };
    if (canUseBrowserStorage()) {
      localStorage.setItem(TOKEN_STORAGE_KEY, snapshot.token);
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(snapshot.user));
    }
    return snapshot;
  } catch {
    clearAuthSnapshot();
    return null;
  }
}

export function writeAuthSnapshot(token: string, user: StoredUser): void {
  if (canUseBrowserStorage()) {
    localStorage.setItem(TOKEN_STORAGE_KEY, token);
    localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
  }
  writeCookie(TOKEN_COOKIE_KEY, token);
  writeCookie(USER_COOKIE_KEY, JSON.stringify(user));
}

export function clearAuthSnapshot(): void {
  if (canUseBrowserStorage()) {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    localStorage.removeItem(USER_STORAGE_KEY);
  }
  deleteCookie(TOKEN_COOKIE_KEY);
  deleteCookie(USER_COOKIE_KEY);
}

export function readAuthToken(): string | null {
  return readAuthSnapshot()?.token || null;
}

